import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Privilege } from '../../api';
import useSession from '../Util/useSession';
import Progress from '../Util/Progress';
import useHasPrivilege from '../Util/useHasPrivilege';
import videosService from '../../services/videos';
import useTranscodeStatus, { TranscodeProgress, isTranscodeBusy } from './useTranscodeStatus';
import TranscodeStatusList, { backendLabel, displayState, useTick } from './TranscodeStatusList';
import {
  Fill,
  FlowCard,
  FlowHeader,
  FlowOverall,
  Mono,
  SessionLabel,
  StatusPill,
  StepState,
  SummaryActions,
  SummaryDetails,
  SummaryFacts,
  SummaryTitle,
  SummaryTitleRow,
  SummaryTop,
  SummaryWrapper,
  Track,
  formatElapsed,
  formatTime,
} from '../Runs/RunStatusUi';

const Empty = styled.div`
  color: #64748b;
  font-size: 14px;
`;

type TParams = { session: string };

interface Props extends RouteComponentProps<TParams> {}

const overallStatus = (states: StepState[], busy: boolean) => {
  if (states.includes('failed')) return { status: 'FAILED', state: 'failed' as StepState, label: 'Failed' };
  if (busy && states.includes('running')) return { status: 'RUNNING', state: 'running' as StepState, label: 'Running' };
  if (busy) return { status: 'QUEUED', state: 'queued' as StepState, label: 'Queued' };
  if (states.length > 0 && states.every((s) => s === 'done')) return { status: 'SUCCEEDED', state: 'done' as StepState, label: 'Done' };
  return { status: 'CANCELLED', state: 'waiting' as StepState, label: 'Not started' };
};

const transcoderLabel = (items: TranscodeProgress[]) => {
  const labels = Array.from(
    new Set(items.filter((x) => x.backend).map((x) => [backendLabel(x.backend), x.pipeline].filter(Boolean).join(' · ')))
  );
  return labels.join(', ');
};

const SessionTranscodeStatus: React.FunctionComponent<Props> = ({ match, history }) => {
  const sessionId = match.params.session;
  const { enqueueSnackbar } = useSnackbar();
  const isAdmin = useHasPrivilege(Privilege.Admin);
  const [retranscoding, setRetranscoding] = useState(false);
  const { session } = useSession(sessionId);
  const { data, error, reload } = useTranscodeStatus(sessionId);
  const busy = !!data && data.some(isTranscodeBusy);
  useTick(busy);

  if (error && !data) {
    return (
      <div className="sciui-card sciui-m-l sciui-bg-p-5">
        <SummaryWrapper>
          <Empty>Failed to load the transcode status.</Empty>
        </SummaryWrapper>
      </div>
    );
  }

  if (!data) {
    return <Progress />;
  }

  const states = data.map(displayState);
  const overall = overallStatus(states, busy);
  const done = states.filter((s) => s === 'done').length;
  const running = states.filter((s) => s === 'running').length;
  const percent = data.length
    ? Math.floor(data.reduce((sum, x, i) => sum + (states[i] === 'done' ? 100 : x.percent || 0), 0) / data.length)
    : 0;
  const starts = data.map((x) => x.startedAt || x.createdAt).filter((x): x is number => !!x);
  const finishes = data.map((x) => x.finishedAt).filter((x): x is number => !!x);
  const startedAt = starts.length ? Math.min(...starts) : undefined;
  const finishedAt = !busy && finishes.length ? Math.max(...finishes) : undefined;
  const transcoder = transcoderLabel(data);
  const withJob = data.map((x) => ({ item: x })).filter((x) => x.item.jobId);
  const orchestratorRuns = withJob.filter((x) => x.item.backend === 'Orchestrator');

  const onRetranscode = async () => {
    try {
      setRetranscoding(true);
      await videosService.retranscode(sessionId);
      window.setTimeout(reload, 2000);
    } catch (e) {
      enqueueSnackbar('Failed to start transcoding', { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setRetranscoding(false);
    }
  };

  return (
    <Fragment>
      <div className="sciui-card sciui-m-l sciui-bg-p-5">
        <SummaryWrapper>
          <SummaryTop>
            <SummaryTitle>
              <SummaryTitleRow>
                <h2>
                  {session?.homeTeam?.name || session?.awayTeam?.name
                    ? `${session?.homeTeam?.name || '?'} – ${session?.awayTeam?.name || '?'}`
                    : `Session ${sessionId}`}
                </h2>
                {session?.label && <SessionLabel>{session.label}</SessionLabel>}
                <StatusPill status={overall.status} label={overall.label} />
              </SummaryTitleRow>
              <SummaryFacts>
                <span>Session {sessionId}</span>
                {session?.pitch?.name && <span>{session.pitch.name}</span>}
                {transcoder && <span>Transcoder {transcoder}</span>}
              </SummaryFacts>
            </SummaryTitle>
            <SummaryActions>
              <button
                type="button"
                className="sciui-button sciui-button--secondary"
                onClick={() => history.push('/session/upload/' + sessionId)}
              >
                Videos
              </button>
              {isAdmin && !busy && (
                <button
                  type="button"
                  className="sciui-button sciui-button--primary"
                  onClick={onRetranscode}
                  disabled={retranscoding}
                >
                  {retranscoding ? <Progress /> : 'Retranscode'}
                </button>
              )}
            </SummaryActions>
          </SummaryTop>
          <SummaryDetails>
            <div>
              <dt>Started</dt>
              <dd>{formatTime(startedAt)}</dd>
            </div>
            <div>
              <dt>{busy ? 'Elapsed' : 'Duration'}</dt>
              <dd>{busy || finishedAt ? formatElapsed(startedAt, finishedAt) : '—'}</dd>
            </div>
            <div>
              <dt>Finished</dt>
              <dd>{formatTime(finishedAt)}</dd>
            </div>
            <div>
              <dt>Run</dt>
              <dd>
                {withJob.length
                  ? withJob.map((x) => (
                      <div key={x.item.videoId}>
                        <Mono>{x.item.jobId}</Mono>
                      </div>
                    ))
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>Orchestrator run</dt>
              <dd>
                {orchestratorRuns.length
                  ? orchestratorRuns.map((x) => (
                      <div key={x.item.videoId}>
                        {x.item.jobPageUrl ? (
                          <a href={x.item.jobPageUrl} target="_blank" rel="noopener noreferrer">
                            <Mono>{x.item.jobId}</Mono>
                          </a>
                        ) : (
                          <Mono>{x.item.jobId}</Mono>
                        )}
                      </div>
                    ))
                  : '—'}
              </dd>
            </div>
          </SummaryDetails>
        </SummaryWrapper>
      </div>
      <div className="sciui-card sciui-m-l sciui-bg-p-5">
        <FlowCard>
          <FlowHeader>
            <h2>Transcode</h2>
          </FlowHeader>
          {data.length === 0 ? (
            <Empty>This session has no videos yet.</Empty>
          ) : (
            <>
              <FlowOverall>
                <div>
                  <span>
                    {done} of {data.length} videos done{running ? ` · ${running} running` : ''}
                  </span>
                  <strong>{percent}%</strong>
                </div>
                <Track>
                  <Fill percent={percent} state={overall.state} />
                </Track>
              </FlowOverall>
              <TranscodeStatusList items={data} />
            </>
          )}
        </FlowCard>
      </div>
    </Fragment>
  );
};

export default withRouter(SessionTranscodeStatus);
